import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Card, CardContent, IconButton, Stack, Tab, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useMemo } from "react";
import {
  ApiTab,
  ApplicationTab,
  ArgusTab,
  BillingTab,
  CITab,
  CLITab,
  ContextTab,
  DataAssetTab,
  ExtensionTab,
  GlobalVariablesTab,
  IntegrationsTab,
  NotificationsTab,
  WebhooksTab,
} from "../components/configuration";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useMe } from "../hooks/use-me.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { ApiConfig } from "../types.ts";
import PersonasPage from "./personas.tsx";

interface ConfigurationTabs {
  tab?:
    | "config"
    | "extension"
    | "notifications"
    | "webhooks"
    | "ci"
    | "cli"
    | "api"
    | "context"
    | "data-assets"
    | "integrations"
    | "billing"
    | "global-variables"
    | "argus";
}

export default function ConfigurationPage() {
  const { params, setParam } = useTypedQueryParams<ConfigurationTabs>();
  const value = params.tab ?? "config";

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setParam("tab", newValue as ConfigurationTabs["tab"]);
  };

  const { enqueueSnackbar } = useSnackbar();
  const { data: me } = useMe();

  const { data, isLoading, refetch } = useApiQuery<ApiConfig>("/config");

  const maybeApiKey = useMemo(() => {
    return data?.api_keys?.[0]?.key;
  }, [data]);

  return (
    <Box data-testid="configuration-page">
      <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="column" spacing={0} alignItems="flex-start">
          <Typography variant="h5">Settings</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Configure your application settings and credentials
          </Typography>
        </Stack>
        {!!me?.organization?.id && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Current Organization ID">
              <Typography variant="caption">
                <code>{me.organization.id}</code>
              </Typography>
            </Tooltip>
            <Tooltip title="Copy Organization ID to clipboard">
              <IconButton
                size="small"
                color="inherit"
                disabled={!me?.organization?.id}
                onClick={() => {
                  if (!me?.organization?.id) return;
                  navigator.clipboard.writeText(me.organization.id);
                  enqueueSnackbar("Organization ID copied to clipboard", {
                    variant: "success",
                    anchorOrigin: {
                      horizontal: "right",
                      vertical: "bottom",
                    },
                  });
                }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={handleTabChange}
            aria-label="Test tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Application" value="config" />
            {/* <Tab label="Extension" value="extension" /> */}
            <Tab label="Integrations" value="integrations" />
            <Tab label="Notifications" value="notifications" />
            {/* <Tab label="Webhooks" value="webhooks" /> */}
            <Tab label="CI" value="ci" />
            <Tab label="CLI" value="cli" />
            <Tab label="API" value="api" />
            <Tab label="Contexts" value="context" />
            <Tab label="Personas" value="personas" />
            <Tab label="Data Assets" value="data-assets" />
            <Tab label="Global Variables" value="global-variables" />
            <Tab label="Billing" value="billing" />
            <Tab label="Argus" value="argus" />
          </TabList>
        </Box>
        <TabPanel value="config">
          <ApplicationTab data={data} isLoading={isLoading} refetch={refetch} />
        </TabPanel>
        <TabPanel value="extension">
          <ExtensionTab apiDisabled={isLoading || !maybeApiKey} />
        </TabPanel>
        <TabPanel value="integrations">
          <IntegrationsTab />
        </TabPanel>
        <TabPanel value="notifications">
          <NotificationsTab />
        </TabPanel>
        <TabPanel value="webhooks">
          <WebhooksTab />
        </TabPanel>
        <TabPanel value="ci">
          <CITab maybeApiKey={maybeApiKey} />
        </TabPanel>
        <TabPanel value="cli">
          <CLITab maybeApiKey={maybeApiKey} />
        </TabPanel>
        <TabPanel value="api">
          <ApiTab />
        </TabPanel>
        <TabPanel value="context">
          <ContextTab />
        </TabPanel>
        <TabPanel value="personas">
          <Card>
            <CardContent>
              <PersonasPage />
            </CardContent>
          </Card>
        </TabPanel>
        <TabPanel value="data-assets">
          <DataAssetTab />
        </TabPanel>
        <TabPanel value="global-variables">
          <GlobalVariablesTab />
        </TabPanel>
        <TabPanel value="billing">
          <BillingTab />
        </TabPanel>
        <TabPanel value="argus">
          <ArgusTab />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
